import React, { useState, useEffect, useCallback } from 'react';
import { X, Loader2, CheckSquare, Square, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';
import CategorySelector from './CategorySelector';
import EavAttributeField from './EavAttributeField';
import AttributeSetSelector from './AttributeSetSelector';
import SupplierSelect from './SupplierSelect';

/**
 * BatchUpdateModal
 *
 * Shows all editable fields (DB columns + EAV attributes) for an entity type.
 * Users can check which fields to update and enter new values.
 * For products with different attribute sets, only common EAV attributes are shown.
 *
 * Props:
 *   entityType   - 'products' | 'suppliers' | 'employees'
 *   selectedIds  - array of entity IDs
 *   onClose      - () => void
 *   onSuccess    - () => void
 *   apiPrefix    - base API path (defaults to /api/{entityType})
 */
const BatchUpdateModal = ({ entityType, selectedIds, onClose, onSuccess, apiPrefix, storeId }) => {
  const { t, lang } = useTranslation();
  const { notify } = useNotification();
  const apiBase = apiPrefix || `/api/${entityType}`;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [error, setError] = useState(null);

  // Form config from API
  const [dbColumns, setDbColumns] = useState([]);
  const [eavAttributes, setEavAttributes] = useState([]);
  const [commonAttributeSetId, setCommonAttributeSetId] = useState(null);

  // Checked state: { fieldCode: true/false }
  const [checkedFields, setCheckedFields] = useState({});

  // Values: { fieldCode: 'value' }
  const [fieldValues, setFieldValues] = useState({});



  // Load form config on mount
  useEffect(() => {
    const fetchConfig = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        selectedIds.forEach(id => params.append('ids[]', id));
        if (storeId) params.set('store_id', storeId);
        const res = await fetch(`${apiBase}/batch-form-config?${params.toString()}`);
        const data = await res.json();
        if (data.status !== 'success') {
          setError(data.message || 'Failed to load form config');
          return;
        }

        const config = data.data;
        setDbColumns(config.dbColumns || []);
        setEavAttributes(config.eavAttributes || []);
        setCommonAttributeSetId(config.commonAttributeSetId);

        // Initialize checked state - all unchecked by default
        const initialChecked = {};
        const initialValues = {};
        (config.dbColumns || []).forEach(col => {
          initialChecked[col.code] = false;
          initialValues[col.code] = '';
        });
        (config.eavAttributes || []).forEach(attr => {
          initialChecked[attr.attribute_code] = false;
          initialValues[attr.attribute_code] = '';
        });
        setCheckedFields(initialChecked);
        setFieldValues(initialValues);


      } catch (e) {
        setError(e.message || 'Failed to load form config');
      } finally {
        setLoading(false);
      }
    };

    if (selectedIds && selectedIds.length > 0) {
      fetchConfig();
    }
  }, [apiBase, selectedIds]);

  const handleCheckAll = useCallback(() => {
    const newChecked = {};
    Object.keys(checkedFields).forEach(key => {
      newChecked[key] = true;
    });
    setCheckedFields(newChecked);
  }, [checkedFields]);

  const handleUncheckAll = useCallback(() => {
    const newChecked = {};
    Object.keys(checkedFields).forEach(key => {
      newChecked[key] = false;
    });
    setCheckedFields(newChecked);
  }, [checkedFields]);

  const toggleField = (code) => {
    setCheckedFields(prev => ({ ...prev, [code]: !prev[code] }));
  };

  const updateValue = (code, value) => {
    setFieldValues(prev => ({ ...prev, [code]: value }));
  };

  const handleSubmit = async () => {
    // Build updates object from checked fields
    const updates = {};
    Object.entries(checkedFields).forEach(([code, checked]) => {
      if (checked) {
        let val = fieldValues[code] || '';
        // Convert comma-separated images to array
        if (code === 'images') {
          val = val ? val.split(',').filter(Boolean) : [];
        }
        updates[code] = val;
      }
    });

    if (Object.keys(updates).length === 0) {
      notify(t('common.select_at_least_one_field'), 'error');
      return;
    }

    setSubmitting(true);
    setProgress({ current: 0, total: 1 });

    try {
      const res = await fetch(`${apiBase}/batch-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedIds, updates }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        setProgress({ current: 1, total: 1 });
        notify(t('common.success'), 'success');
        onSuccess && onSuccess();
      } else {
        setError(data.message || t('common.error'));
        notify(data.message || t('common.error'), 'error');
      }
    } catch (e) {
      setError(e.message || t('common.error'));
      notify(e.message || t('common.error'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Count checked fields
  const checkedCount = Object.values(checkedFields).filter(Boolean).length;
  const totalFields = Object.keys(checkedFields).length;

  // Render input for a DB column based on its type
  const renderDbColumnInput = (col) => {
    const value = fieldValues[col.code] || '';

    const isChecked = checkedFields[col.code] || false;
    const disabled = !isChecked;

    const renderComplexField = (checked) => {
      const complexDisabled = !checked;
      const wrapClass = complexDisabled ? 'pointer-events-none opacity-60' : '';

      switch (col.code) {
        case 'category_id':
          return (
            <div className={wrapClass}>
              <CategorySelector
                value={value || ''}
                onChange={(val) => updateValue(col.code, val)}
              />
            </div>
          );
        case 'attribute_set_id':
          const entityType = apiBase.includes('products') ? 'product' :
            apiBase.includes('suppliers') ? 'supplier' : 'employee';
          return (
            <div className={wrapClass}>
              <AttributeSetSelector
                value={value || ''}
                onChange={(val) => updateValue(col.code, val)}
                entityType={entityType}
              />
            </div>
          );
        case 'supplier_id':
          return (
            <div className={wrapClass}>
              <SupplierSelect
                value={value || ''}
                onChange={(val) => updateValue(col.code, val)}
              />
            </div>
          );
        case 'images':
          return (
            <div className={wrapClass}>
              <ImageBatchInput
                value={value || ''}
                onChange={(val) => updateValue(col.code, val)}
              />
            </div>
          );
        default:
          return null;
      }
    };

    // Handle complex field components that need disabled wrapping
    if (['category_id', 'attribute_set_id', 'supplier_id', 'images'].includes(col.code)) {
      return renderComplexField(isChecked);
    }

    switch (col.code) {

      case 'species':
        return (
          <select
            value={value}
            onChange={(e) => updateValue(col.code, e.target.value)}
            disabled={disabled}
            className={`w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white ${
              disabled
                ? 'border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800/50 text-slate-400 dark:text-slate-500 cursor-not-allowed'
                : 'border-slate-300 dark:border-slate-600'
            }`}
          >
            <option value="">--</option>
            <option value="human">Human</option>
            <option value="ai">AI</option>
          </select>
        );

      case 'status':
        return (
          <select
            value={value}
            onChange={(e) => updateValue(col.code, e.target.value)}
            disabled={disabled}
            className={`w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white ${
              disabled
                ? 'border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800/50 text-slate-400 dark:text-slate-500 cursor-not-allowed'
                : 'border-slate-300 dark:border-slate-600'
            }`}
          >
            <option value="">--</option>
            <option value="1">Active</option>
            <option value="0">Frozen</option>
          </select>
        );

      case 'hire_date':
      case 'created_at':
      case 'updated_at':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => updateValue(col.code, e.target.value)}
            disabled={disabled}
            className={`w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white ${
              disabled
                ? 'border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800/50 text-slate-400 dark:text-slate-500 cursor-not-allowed'
                : 'border-slate-300 dark:border-slate-600'
            }`}
          />
        );

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => updateValue(col.code, e.target.value)}
            disabled={disabled}
            className={`w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white ${
              disabled
                ? 'border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800/50 text-slate-400 dark:text-slate-500 cursor-not-allowed'
                : 'border-slate-300 dark:border-slate-600'
            }`}
            placeholder={col.label}
          />
        );
    }
  };

  // Render input for an EAV attribute — delegates to EavAttributeField
  const renderEavAttributeInput = (attr) => {
    const value = fieldValues[attr.attribute_code] || '';
    const isChecked = checkedFields[attr.attribute_code] || false;

    return (
      <div className={isChecked ? '' : 'pointer-events-none opacity-60'}>
        <EavAttributeField
          attr={attr}
          value={value}
          hideLabel={true}
          onChange={(code, val) => updateValue(code, val)}
        />
      </div>
    );
  };

  // Get localized label for a field
  const getFieldLabel = (field) => {
    // For EAV attributes with translations, use the current language label
    if (field.translations && field.translations[lang]) {
      return field.translations[lang];
    }
    // Fallback to label or code
    return field.label || field.attribute_code || field.code;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-8 flex items-center gap-3">
          <Loader2 className="animate-spin text-blue-500" size={24} />
          <span className="text-slate-600 dark:text-slate-300">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-white">
              {t('common.batch_update_attributes')}
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {t('common.selected_count')} {selectedIds.length} {t('common.items')}
              {totalFields > 0 && ` · ${t('common.fields', {checked: checkedCount, total: totalFields})}`}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
            <X size={20} className="text-slate-500" />
          </button>
        </div>

        {/* Progress bar */}
        {submitting && (
          <div className="px-6 py-2 bg-blue-50 dark:bg-blue-900/20">
            <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
              <Loader2 className="animate-spin" size={16} />
              <span>{t('common.saving')}</span>
            </div>
            {progress.total > 0 && (
              <div className="mt-1 h-1.5 bg-blue-200 dark:bg-blue-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all duration-300"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Select All / Deselect All */}
        <div className="px-6 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center gap-4">
          <button
            onClick={handleCheckAll}
            className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 transition-colors"
          >
            <CheckSquare size={14} />
            {t('common.select_all')}
          </button>
          <button
            onClick={handleUncheckAll}
            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 transition-colors"
          >
            <Square size={14} />
            {t('common.deselect_all')}
          </button>
        </div>

        {/* Fields list */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
          {/* DB Column Fields */}
          {dbColumns.length > 0 && (
            <div className="mb-4">
              <h3 className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2">
                {t('common.basic_fields')}
              </h3>
              {dbColumns.map(col => (
                <div
                  key={col.code}
                  className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${
                    checkedFields[col.code]
                      ? 'bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800'
                      : 'hover:bg-slate-50 dark:hover:bg-slate-800/50 border border-transparent'
                  }`}
                >
                  <label className="flex items-center gap-2 pt-2 cursor-pointer min-w-[120px] flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={checkedFields[col.code] || false}
                      onChange={() => toggleField(col.code)}
                      className="rounded"
                    />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap">
                      {col.label || col.code}
                    </span>
                  </label>
                  <div className="flex-1">
                    {renderDbColumnInput(col)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* EAV Attribute Fields */}
          {eavAttributes.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2">
                {t('common.eav_attributes')}
              </h3>
              {eavAttributes.map(attr => (
                <div
                  key={attr.attribute_code}
                  className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${
                    checkedFields[attr.attribute_code]
                      ? 'bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800'
                      : 'hover:bg-slate-50 dark:hover:bg-slate-800/50 border border-transparent'
                  }`}
                >
                  <label className="flex items-center gap-2 pt-2 cursor-pointer min-w-[120px] flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={checkedFields[attr.attribute_code] || false}
                      onChange={() => toggleField(attr.attribute_code)}
                      className="rounded"
                    />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap">
                      {getFieldLabel(attr)}
                    </span>
                  </label>
                  <div className="flex-1">
                    {renderEavAttributeInput(attr)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalFields === 0 && !loading && (
            <div className="text-center py-12 text-slate-400 dark:text-slate-500">
              <p>{t('common.no_editable_fields')}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || checkedCount === 0}
            className={`px-6 py-2 text-sm font-medium text-white rounded-lg transition-all ${
              submitting || checkedCount === 0
                ? 'bg-blue-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 shadow-sm'
            }`}
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <Loader2 className="animate-spin" size={16} />
                {t('common.saving')}
              </span>
            ) : (
              t('common.save') + ` (${checkedCount})`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Small inline component for batch image editing ── */
const ImageBatchInput = ({ value, onChange }) => {
  const images = value ? value.split(',').filter(Boolean) : [];

  const handleAddUrl = () => {
    const url = prompt('Enter image URL:');
    if (url && url.trim()) {
      const updated = [...images, url.trim()];
      onChange(updated.join(','));
    }
  };

  const handleRemove = (idx) => {
    const updated = images.filter((_, i) => i !== idx);
    onChange(updated.join(','));
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2 flex-wrap">
        {images.length === 0 && (
          <div className="text-sm text-slate-400 dark:text-slate-500 italic">
            (No images — will replace all products' images with empty)
          </div>
        )}
        {images.map((url, idx) => (
          <div key={idx} className="relative group w-16 h-16 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
            <img src={url} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => handleRemove(idx)}
              className="absolute top-0.5 right-0.5 p-0.5 bg-red-500/80 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Trash2 size={10} />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={handleAddUrl}
        className="text-xs flex items-center gap-1 text-blue-600 hover:text-blue-700 dark:text-blue-400"
      >
        <Plus size={12} />
        Add Image URL
      </button>
      {images.length > 0 && (
        <input
          type="hidden"
          value={images.join(',')}
        />
      )}
    </div>
  );
};

export default BatchUpdateModal;
